import { useApolloClient } from "@apollo/client";
import { Modal } from "@msys/ui";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import { TextField } from "../../commons/form-fields/TextField.js";
import { useUpdateProductListMutation } from "./EditProductListModal.generated.js";

interface FormValues {
  description: string;
}

export const EditProductListDescriptionModal = ({
  productListId,
  description,
  handleComplete,
  handleClose,
}: {
  productListId: string;
  description: string;
  handleClose(): void;
  handleComplete?(productListId: string): void;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["Global", "ProductLists"]);

  const client = useApolloClient();
  const [update] = useUpdateProductListMutation({ client });

  const handleSubmit = async (values: FormValues) => {
    try {
      const result = await update({
        variables: {
          input: {
            productListId,
            values: {
              description: values.description,
            },
          },
        },
      });
      if (result.data?.updateProductList.productList.id) {
        enqueueSnackbar(
          t("Saved", {
            ns: "Global",
          }),
          { variant: "success" }
        );
        handleComplete?.(result.data?.updateProductList.productList.id);
        handleClose();
      }
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  const initialValues: FormValues = {
    description,
  };

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<FormValues> onSubmit={handleSubmit} initialValues={initialValues}>
      {({ isSubmitting, dirty }) => (
        <Modal
          title={t("Edit description", {
            ns: "ProductLists",
          })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Close", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: { variant: "text" },
            },
            {
              label: t("Save", { ns: "Global" }),
              buttonProps: {
                loading: isSubmitting,
                form: formId,
                type: "submit",
                disabled: !dirty,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack spacing={1} direction="column">
              <TextField
                name="description"
                multiline
                minRows={4}
                label={t("Description", {
                  ns: "Global",
                })}
              />
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
